import { alpha, useMantineTheme } from "@mantine/core";
import type { BarSeriesOption } from "echarts";
import { useMemo } from "react";

export const useMemoizedBarSeries = (
  data: [number, number][] = [],
  name?: string,
  baseline?: number,
  baselineLabel?: string,
): BarSeriesOption[] => {
  const { colors } = useMantineTheme();

  return useMemo(() => {
    if (baseline === undefined) {
      return [
        {
          type: "bar",
          name,
          data,
          itemStyle: {
            color: colors.purpleBlue[6],
            borderRadius: 20,
          },
        },
      ];
    }

    const baselineSeries: BarSeriesOption = {
      type: "bar",
      name: baselineLabel,
      data: data.map(([x, y]) => [x, y > baseline ? baseline : y]),
      stack: name,
      itemStyle: {
        color: alpha(colors.neonGreen[4], 0.8), // design says 6, but that looks off
      },
      markLine: {
        silent: true,
        symbol: ["none", "none"],
        lineStyle: {
          type: "dashed",
          color: colors.gray[9],
          width: 1,
        },
        data: [{ yAxis: baseline }],
      },
    };

    const remainderSeries: BarSeriesOption = {
      type: "bar",
      name,
      data: data.map(([x, y]) => [x, y > baseline ? y - baseline : 0]),
      stack: name,
      itemStyle: {
        color: colors.purpleBlue[1], // design says 0, but that's too light
      },
    };

    const borderSeries: BarSeriesOption = {
      data,
      type: "bar",
      barGap: "-100%",
      itemStyle: {
        color: "transparent",
        borderColor: colors.purpleBlue[9],
        borderWidth: 1,
      },
      z: 3,
      silent: true,
      tooltip: { show: false },
    };

    return [baselineSeries, remainderSeries, borderSeries];
  }, [data, baseline, name, baselineLabel]);
};

export type StackedBarLayer = {
  name: string;
  data: [number, number][];
  color: string;
  id: string;
};

export const useMemoizedStackedBarSeries = (
  layers: StackedBarLayer[],
  stackName = "stack",
): BarSeriesOption[] =>
  useMemo(() =>
    layers.map(layer => ({
      type: "bar",
      name: layer.name,
      data: layer.data,
      stack: stackName,
      itemStyle: {
        color: layer.color,
      },
    })), [layers, stackName]);

export function normalizeStackedLayers(layers: StackedBarLayer[]): StackedBarLayer[] {
  // 1. Collect all unique timestamps across all layers
  const allTimestamps = Array.from(
    new Set(layers.flatMap(layer => layer.data.map(([ts]) => ts))),
  ).sort((a, b) => a - b);

  // 2. For each layer, fill in 0 for any missing timestamp
  return layers.map((layer) => {
    const dataMap = new Map(layer.data.map(([ts, val]) => [ts, val]));
    return {
      ...layer,
      data: allTimestamps.map(ts => [ts, dataMap.get(ts) ?? 0]),
    };
  });
}
